import axios from "axios";
import { buildApiUrl } from "./apiConfig";
import { getEffectivePortCoordinates } from "./tourPortLocations";

const normalizeWeatherbitTime = (item) => {
  const local = item?.timestamp_local;
  if (local) return String(local);
  const utc = item?.timestamp_utc;
  if (!utc) return null;
  const text = String(utc);
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(text) ? text : `${text}Z`;
};

export const normalizeWeatherbitForecast = (payload) => {
  const forecast = payload?.forecast || payload;
  const data = Array.isArray(forecast?.data) ? forecast.data : [];
  if (!data.length) return null;

  const hourly = {
    time: [],
    temperature_2m: [],
    precipitation: [],
    cloud_cover: [],
    wind_speed_10m: [],
    wind_direction_10m: [],
    uv_index: [],
  };

  data.forEach((item) => {
    const time = normalizeWeatherbitTime(item);
    if (!time) return;
    hourly.time.push(time);
    hourly.temperature_2m.push(Number.isFinite(Number(item?.temp)) ? Number(item.temp) : null);
    hourly.precipitation.push(Number.isFinite(Number(item?.precip)) ? Number(item.precip) : null);
    hourly.cloud_cover.push(Number.isFinite(Number(item?.clouds)) ? Number(item.clouds) : null);
    hourly.wind_speed_10m.push(Number.isFinite(Number(item?.wind_spd)) ? Number(item.wind_spd) * 3.6 : null);
    hourly.wind_direction_10m.push(Number.isFinite(Number(item?.wind_dir)) ? Number(item.wind_dir) : null);
    hourly.uv_index.push(Number.isFinite(Number(item?.uv)) ? Number(item.uv) : null);
  });

  return {
    latitude: payload?.location?.coordinates?.[1] ?? forecast?.lat,
    longitude: payload?.location?.coordinates?.[0] ?? forecast?.lon,
    timezone: forecast?.timezone || payload?.response_metadata?.timezone || null,
    hourly_units: {
      temperature_2m: '°C',
      precipitation: 'mm',
      cloud_cover: '%',
      wind_speed_10m: 'km/h',
      wind_direction_10m: '°',
      uv_index: '',
    },
    hourly,
  };
};

/**
 * Fetch hourly forecast for a coordinate pair and date range
 * coordinates: [lng, lat]
 * start, end: ISO strings (e.g., '2025-09-20T00:00:00Z')
 * getApiHeaders: async function that returns auth headers
 */
export const fetchWeather = async (coordinates, start, end, getApiHeaders) => {
  if (!coordinates || coordinates.length !== 2 || !start || !end) return null;
  try {
    const headers = getApiHeaders ? await getApiHeaders() : {};
    const params = new URLSearchParams({
      latitude: String(coordinates[1]),
      longitude: String(coordinates[0]),
    });
    const url = buildApiUrl(`weatherbit-forecast/?${params.toString()}`);
    const res = await axios({ url, method: 'get', headers });
    return normalizeWeatherbitForecast(res.data);
  } catch (err) {
    // Swallow and return null to keep UI resilient
    return null;
  }
};

export const fetchOpenMeteoWeather = async (coordinates, start, end, getApiHeaders) => {
  if (!coordinates || coordinates.length !== 2 || !start || !end) return null;
  try {
    const headers = getApiHeaders ? await getApiHeaders() : {};
    const params = new URLSearchParams({
      latitude: String(coordinates[1]),
      longitude: String(coordinates[0]),
    });
    const url = buildApiUrl(`weatherbit-forecast/?${params.toString()}`);
    const res = await axios({ url, method: 'get', headers });
    return normalizeWeatherbitForecast(res.data);
  } catch {
    return null;
  }
};

/**
 * Fetch hourly marine forecast for a coordinate pair and date range.
 * coordinates: [lng, lat]
 */
export const fetchMarineForecast = async (coordinates, start, end, getApiHeaders) => {
  if (!coordinates || coordinates.length !== 2 || !start || !end) return null;
  try {
    const params = new URLSearchParams({
      latitude: String(coordinates[1]),
      longitude: String(coordinates[0]),
      hourly: [
        'wave_height',
        'wave_direction',
        'wave_period',
        'wind_wave_height',
        'wind_wave_direction',
        'wind_wave_period',
        'swell_wave_height',
        'swell_wave_direction',
        'swell_wave_period',
      ].join(','),
      start_date: start.split('T')[0],
      end_date: end.split('T')[0],
    });
    const url = `https://marine-api.open-meteo.com/v1/marine?${params.toString()}`;
    // Open-Meteo marine is called directly from the browser; custom app headers
    // would trigger a CORS preflight that their endpoint does not allow.
    const res = await axios({ url, method: 'get' });
    return res.data;
  } catch (err) {
    return null;
  }
};

/**
 * Given an array of ports_of_call, resolve weather for each that has coordinates and dates.
 * Returns a new array with { ...poc, weather } for those resolvable, original poc otherwise.
 * getApiHeaders: async function that returns auth headers
 */
export const addWeatherToPorts = async (portsOfCall, getApiHeaders) => {
  if (!Array.isArray(portsOfCall)) return [];
  const updated = await Promise.all(
    portsOfCall.map(async (poc) => {
      try {
        const coords = getEffectivePortCoordinates(poc);
        const start = poc?.day_arrival;
        const end = poc?.day_departure;
        if (coords && start && end) {
          const weather = await fetchWeather(coords, start, end, getApiHeaders);
          return { ...poc, weather };
        }
        return poc;
      } catch {
        return poc;
      }
    })
  );
  return updated;
};
